import { useEffect, useState } from "react";

import {
  Mail,
  Phone,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Home,
  ArrowUp,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import ChaafoLogo from "../Images/chaafo-logo-footer.svg";

// ====================================================
// QUICK LINKS
// ====================================================

const quickLinks = [
  {
    id: 1,
    label: "Home",
    path: "/",
  },
  {
    id: 2,
    label: "About us",
    path: "/about",
  },
  {
    id: 3,
    label: "Careers",
    path: "https://enrrol.com/",
  },
  {
    id: 4,
    label: "Chaafo Instsamart",
    path: "https://chaafoinstamart.com/",
  },
  {
    id: 5,
    label: "Restaurants",
    path: "/#all-restaurants",
  },
  {
    id: 6,
    label: "Blogs",
    path: "https://chaafo.com/blogs/",
  },
];

// =========================================================
// SUPPORT LINKS
// =========================================================

const supportLinks = [
  {
    id: 1,
    label: "Privacy Policy",
    path: "https://chaafo.com/privacy-policy/",
  },
  {
    id: 2,
    label: "Refund Policy",
    path: "https://chaafo.com/refund-policy/",
  },
  {
    id: 3,
    label: "Cancellation Policy",
    path: "https://chaafo.com/cancellation-policy/",
  },
  {
    id: 4,
    label: "Terms & Conditions",
    path: "https://chaafo.com/terms-and-conditions/",
  },
  {
    id: 5,
    label: "Delete Account",
    path: "https://chaafo.com/delete-account/",
  },
];

// =========================================================
// ADDITIONAL LINKS
// =========================================================

const additionalLinks = [
  {
    id: 1,
    label:
      "2nd Floor, Tower B, I-Thum Tower, Plot No. A-40, Sec- 62, Noida, UttarPradesh,201301",
    href: "#download",
    icon: Home,
  },
  {
    id: 2,
    label: "support@chaafo.com",
    href: "mailto:support@chaafo.com",
    icon: Mail,
  },
  {
    id: 3,
    label: "+91 - 95990 77705",
    href: "tel:+919599077705",
    icon: Phone,
  },
];

// =========================================================
// SOCIAL LINKS
// =========================================================

const socialLinks = [
  {
    id: 1,
    label: "Facebook",
    href: "https://facebook.com",
    icon: Facebook,
  },
  {
    id: 2,
    label: "Instagram",
    href: "https://instagram.com",
    icon: Instagram,
  },
  {
    id: 3,
    label: "LinkedIn",
    href: "https://linkedin.com",
    icon: Linkedin,
  },
  {
    id: 4,
    label: "YouTube",
    href: "https://youtube.com",
    icon: Youtube,
  },
];

// =========================================================
// FOOTER
// =========================================================

export default function Footer() {
  const navigate = useNavigate();

  const [scrollProgress, setScrollProgress] = useState(0);

  // =======================================================
  // NAVIGATION
  // =======================================================

  const handleNavigation = (path) => {
    if (!path) {
      return;
    }

    const isExternalUrl =
      path.startsWith("http://") || path.startsWith("https://");

    if (isExternalUrl) {
      window.open(path, "_blank", "noopener,noreferrer");

      return;
    }

    if (path === "/#all-restaurants") {
      navigate("/");

      window.setTimeout(() => {
        document.getElementById("all-restaurants")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);

      return;
    }

    navigate(path);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // SCROLL PROGRESS
  // =========================================================

  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;

      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    updateScrollProgress();

    window.addEventListener("scroll", updateScrollProgress, {
      passive: true,
    });

    window.addEventListener("resize", updateScrollProgress);

    return () => {
      window.removeEventListener("scroll", updateScrollProgress);

      window.removeEventListener("resize", updateScrollProgress);
    };
  }, []);

  // =========================================================
  // UI
  // =========================================================

  return (
    <footer
      id="site-footer"
      data-site-footer="true"
      className="w-full bg-[linear-gradient(115deg,#191447_0%,#252b66_48%,#365897_100%)] px-5 py-10 text-white sm:px-8 md:py-14 lg:px-10"
    >
      <div className="mx-auto w-full max-w-[1600px]">
        {/* ================================================= */}
        {/* MAIN FOOTER CONTENT */}
        {/* ================================================= */}

        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[1.8fr_0.8fr_0.9fr_1fr] lg:gap-16">
          {/* =============================================== */}
          {/* BRAND */}
          {/* =============================================== */}

          <div>
            <div>
              <button
                type="button"
                onClick={() => handleNavigation("/")}
                aria-label="Go to home page"
                className="block w-fit"
              >
                <img
                  src={ChaafoLogo}
                  alt="Chaafo"
                  className="h-auto w-[165px] object-contain sm:w-[185px] md:w-[200px]"
                />
              </button>

              <p className="mt-5 max-w-[500px] text-[17px] leading-[28px] text-white/60 sm:text-[18px] sm:leading-[29px]">
                Chaafo brings food, groceries, and everyday essentials closer to
                your doorstep with a fast, reliable, and partner-friendly
                experience.
              </p>
            </div>
          </div>

          {/* =============================================== */}
          {/* QUICK LINKS */}
          {/* =============================================== */}

          <div>
            <h3 className="text-[16px] font-semibold text-white">
              Quick Links
            </h3>

            <div className="mt-4 flex flex-col gap-2">
              {quickLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => handleNavigation(link.path)}
                  className="w-fit text-left text-[16px] leading-6 text-white/60 transition duration-300 hover:translate-x-1 hover:text-white"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* =============================================== */}
          {/* SUPPORT */}
          {/* =============================================== */}

          <div>
            <h3 className="text-[16px] font-semibold text-white">Support</h3>

            <div className="mt-4 flex flex-col gap-2">
              {supportLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => handleNavigation(link.path)}
                  className="w-fit text-left text-[16px] leading-6 text-white/60 transition duration-300 hover:translate-x-1 hover:text-white"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* =============================================== */}
          {/* ADDITIONAL */}
          {/* =============================================== */}

          <div>
            <h3 className="text-[16px] font-semibold text-white">Additional</h3>

            <div className="mt-5 flex flex-col gap-4">
              {additionalLinks.map((link) => {
                const Icon = link.icon;

                return (
                  <a
                    key={link.id}
                    href={link.href}
                    className="flex w-fit items-center gap-3 text-[16px] text-white/60 transition duration-300 hover:translate-x-1 hover:text-white"
                  >
                    <Icon
                      size={22}
                      strokeWidth={1.7}
                      className="shrink-0 text-white"
                    />

                    <span>{link.label}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* DIVIDER */}
        {/* ================================================= */}

        <div className="mt-8 h-px w-full bg-white/35 md:mt-7" />

        {/* ================================================= */}
        {/* FOOTER BOTTOM */}
        {/* ================================================= */}

        <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[14px] text-white/90 sm:text-[16px]">
            © 2026 Chaafo Inc. All rights reserved.
          </p>

          <div className="flex items-center gap-2">
            {socialLinks.map((social) => {
              const Icon = social.icon;

              return (
                <a
                  key={social.id}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-white text-[#1f2a55] shadow-sm transition duration-300 hover:-translate-y-1 hover:bg-[#f3f5f8] hover:shadow-lg"
                >
                  <Icon size={22} strokeWidth={1.8} />
                </a>
              );
            })}
          </div>
        </div>

        {/* ================================================= */}
        {/* SCROLL TO TOP BUTTON */}
        {/* ================================================= */}

        <div
          className="fixed bottom-4 right-5 z-[99999] flex h-[56px] w-[56px] cursor-pointer items-center justify-center rounded-full p-[5px] shadow-lg transition hover:scale-105"
          style={{
            background: `conic-gradient(
      #1e40af ${scrollProgress}%,
      #bfdbfe 0
    )`,
          }}
          onClick={() => {
            // RestaurantMenu ka category scroll lock release karo
            window.dispatchEvent(new Event("chaafo:scroll-to-top"));

            // Normal smooth page scroll
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: "smooth",
            });
          }}
        >
          <div className="flex h-full w-full items-center justify-center rounded-full bg-white">
            <ArrowUp size={28} className="text-blue-800" />
          </div>
        </div>
      </div>
    </footer>
  );
}
